import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ArrowUpRight, CheckCircle2, Circle } from "lucide-react";
import { PageHead, Card } from "@/pages/app/AppShell";
import { METRIC_DEFS } from "@/components/EventDialog";
import { useAuth } from "@/context/AuthContext";
import { api, money } from "@/lib/api";

const ONBOARD = [
  ["profile", "Complete your company profile", "Add a description, industry and locations so partners can find you.", "/app/profile"],
  ["posted", "Post your first opportunity, brief or event", "Tell partners what you're looking for or what you offer.", "/app/network?new=1"],
  ["reached_out", "Reach out to a company", "Find a partner in Discover and send a partnership request or message.", "/discover"],
];

function Onboarding() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/onboarding").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s || ONBOARD.every(([k]) => s[k])) return null;
  const done = ONBOARD.filter(([k]) => s[k]).length;
  return (
    <Card className="p-6 mb-6" data-testid="onboarding-checklist">
      <p className="kicker">Get started · {done} of {ONBOARD.length} done</p>
      <ul className="mt-4 grid gap-3 md:grid-cols-3">
        {ONBOARD.map(([k, t, d, to]) => (
          <li key={k}>
            <Link to={to} className="flex gap-3 rounded-xl border border-[#2A1240]/10 bg-white p-4 hover:border-[#E0218A]" data-testid={`onboarding-step-${k}`}>
              {s[k] ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-300" /> : <Circle className="h-5 w-5 shrink-0 text-[#E0218A]" />}
              <span><span className={`block text-sm font-semibold text-[#2A1240] ${s[k] ? "line-through opacity-60" : ""}`}>{t}</span><span className="block mt-1 text-xs text-slate-300">{d}</span></span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [matches, setMatches] = useState([]);
  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => setD(null));
    api.get("/opportunities/matches").then((r) => setMatches(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const m = d?.metrics || {};
  const pipeline = d?.pipeline || [];
  const maxCount = Math.max(1, ...pipeline.map((p) => p.count));
  return (
    <div data-testid="overview-page">
      <PageHead kicker={`Good to see you, ${user?.name?.split(" ")[0] || ""}`} title="Your partnerships at a glance">
        <Link to="/app/events?new=1" className="btn-gold !py-2.5" data-testid="overview-post-event">Post event</Link>
        <Link to="/app/network?new=1" className="btn-ghost !py-2.5" data-testid="overview-post-opportunity">Post opportunity</Link>
      </PageHead>
      <Onboarding />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px rounded-2xl overflow-hidden bg-[#2A1240]/10" data-testid="overview-kpis">
        {METRIC_DEFS.map(([k, label, f]) => (
          <div key={k} className="bg-[#FFFFFF] p-5">
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">{label}</p>
            <p className={`mt-2 font-mono text-xl sm:text-2xl ${k === "roi" ? "text-emerald-300" : "text-[#2A1240]"}`} data-testid={`kpi-${k}`}>{f(m[k] ?? 0)}</p>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-slate-400">Totals across {d?.events_count ?? 0} event(s) posted by your company. ROI and CPA are calculated automatically.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-8 p-6">
          <p className="kicker">Spend vs revenue by event</p>
          <div className="mt-4 h-64">
            {d?.series?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.series}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                    <linearGradient id="sp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E0218A" stopOpacity={0.4} /><stop offset="100%" stopColor="#E0218A" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(42,18,64,0.08)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "#a3abb8", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#a3abb8", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid rgba(42,18,64,.12)", borderRadius: 12 }} formatter={(v) => money(v)} />
                  <Area type="monotone" dataKey="revenue" stroke="#10B981" fill="url(#rev)" strokeWidth={2} />
                  <Area type="monotone" dataKey="spend" stroke="#E0218A" fill="url(#sp)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-slate-400" data-testid="overview-chart-empty">Post an event with metrics to see your chart.</div>
            )}
          </div>
        </Card>
        <Card className="lg:col-span-4 p-6" data-testid="overview-pipeline">
          <div className="flex justify-between items-center"><p className="kicker">Pipeline</p><Link to="/app/pipeline" className="text-xs text-slate-300 hover:text-[#2A1240]">Open</Link></div>
          <div className="mt-5 space-y-3">
            {pipeline.map((p) => (
              <div key={p.stage} className="flex items-center gap-3 text-sm">
                <span className="w-24 text-slate-300">{p.stage}</span>
                <div className="flex-1 h-2 rounded-full bg-[#2A1240]/5 overflow-hidden"><div className={`h-full ${p.stage === "Won" ? "bg-emerald-400" : "bg-[#E0218A]"}`} style={{ width: `${(p.count / maxCount) * 100}%` }} /></div>
                <span className="w-6 text-right font-mono text-slate-200">{p.count}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="lg:col-span-4 p-6">
          <p className="kicker">Agreements</p>
          <p className="mt-4 font-display text-4xl" data-testid="overview-agreements">{d?.executed ?? 0}<span className="text-lg text-slate-400"> / {d?.agreements ?? 0} executed</span></p>
          <Link to="/app/agreements" className="mt-4 inline-flex items-center gap-1 text-sm text-[#8E2BC4]">Manage <ArrowUpRight className="h-4 w-4" /></Link>
        </Card>
        <Card className="lg:col-span-4 p-6">
          <p className="kicker">Payments</p>
          <p className="mt-4 font-mono text-3xl text-emerald-300" data-testid="overview-collected">{money(d?.collected, 2)}</p>
          <p className="text-sm text-slate-400">collected · {money(d?.outstanding, 2)} outstanding</p>
        </Card>
        <Card className="lg:col-span-4 p-6" data-testid="overview-matches">
          <p className="kicker">Matching your profile</p>
          {!matches.length && <p className="mt-4 text-sm text-slate-400">Add partnership interests to your <Link to="/app/profile" className="text-[#8E2BC4] underline">profile</Link> to get matched.</p>}
          <div className="mt-4 space-y-3">
            {matches.slice(0, 3).map((o) => (
              <Link key={o.id} to={`/companies/${o.company_id}`} className="block group">
                <p className="text-sm text-[#2A1240] group-hover:text-[#8E2BC4]">{o.title}</p>
                <p className="text-xs text-slate-400">{o.company_name} · matched on {o.matched_on.join(", ")}</p>
              </Link>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
